/**
 * Latency over the window - one series, so no legend: the caption names it.
 *
 * Marks follow the fixed specs: a 2px round-capped line, a 10% wash beneath it rather
 * than a saturated block, and an 8px end dot carrying a 2px ring in the surface colour
 * so it stays legible where it crosses the line.
 *
 * The hover layer is a **crosshair**, not a per-point hit target. Nobody can aim at a
 * 2px line, so the pointer only has to be *closest* in x - the hairline snaps to the
 * nearest sample and the readout follows. That is also what makes it usable on a
 * sparkline 60px tall, where per-point targets would be pinpoints.
 */

import { useCallback, useMemo, useRef, useState } from 'react'

import { cn } from '../../lib/cn'
import { formatMs, formatTime } from '../../lib/format'

export interface SparkPoint {
  t: number
  value: number | null
}

interface SparklineProps {
  points: SparkPoint[]
  height?: number
  /** Mark colour. Defaults to the accent; a stat tile passes the de-emphasis grey. */
  stroke?: string
  /** Label for the tooltip's value row, e.g. "round trip". */
  valueLabel?: string
  className?: string
  /** Draw the wash under the line. Off in a dense stat tile. */
  fill?: boolean
  ariaLabel?: string
}

const PAD_Y = 4

export function Sparkline({
  points,
  height = 56,
  stroke = 'var(--color-accent)',
  valueLabel = 'round trip',
  className,
  fill = true,
  ariaLabel,
}: SparklineProps) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  // A viewBox in abstract units lets the SVG scale to its container without the
  // geometry needing a measured pixel width.
  const width = 300
  const usable = useMemo(
    () => points.filter((point) => point.value !== null) as Array<{ t: number; value: number }>,
    [points],
  )

  const geometry = useMemo(() => {
    if (usable.length === 0) return null

    const values = usable.map((point) => point.value)
    const max = Math.max(...values)
    const min = Math.min(...values)
    // A flat series must not collapse onto the baseline, and a series of zeros must
    // not divide by zero - so the span always has a floor.
    const span = Math.max(max - min, 1)
    const plotHeight = height - PAD_Y * 2

    const x = (index: number) =>
      usable.length === 1 ? width / 2 : (index / (usable.length - 1)) * width
    const y = (value: number) => PAD_Y + plotHeight - ((value - min) / span) * plotHeight

    const coords = usable.map((point, index) => ({ x: x(index), y: y(point.value) }))
    const line = coords
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ')
    const area =
      coords.length > 1
        ? `${line} L${coords[coords.length - 1].x.toFixed(2)} ${height} L${coords[0].x.toFixed(2)} ${height} Z`
        : null

    return { coords, line, area, min, max }
  }, [usable, height])

  const onPointerMove = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (!svgRef.current || usable.length === 0) return
      const box = svgRef.current.getBoundingClientRect()
      const ratio = (event.clientX - box.left) / box.width
      const index = Math.round(ratio * (usable.length - 1))
      setHoverIndex(Math.min(Math.max(index, 0), usable.length - 1))
    },
    [usable.length],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<SVGSVGElement>) => {
      if (usable.length === 0) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      setHoverIndex((current) => {
        const from = current ?? usable.length - 1
        const next = event.key === 'ArrowLeft' ? from - 1 : from + 1
        return Math.min(Math.max(next, 0), usable.length - 1)
      })
    },
    [usable.length],
  )

  if (!geometry) {
    return (
      <p
        className={cn('flex items-center text-xs text-ink-muted', className)}
        style={{ height }}
      >
        No latency samples yet
      </p>
    )
  }

  const last = geometry.coords[geometry.coords.length - 1]
  const hovered = hoverIndex === null ? null : usable[hoverIndex]
  const hoveredCoord = hoverIndex === null ? null : geometry.coords[hoverIndex]

  return (
    <div className={cn('relative', className)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full outline-none"
        style={{ height }}
        role="img"
        aria-label={
          ariaLabel ??
          `${valueLabel} over ${usable.length} samples, ` +
            `low ${formatMs(geometry.min)}, high ${formatMs(geometry.max)}`
        }
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHoverIndex(null)}
        onFocus={() => setHoverIndex(usable.length - 1)}
        onBlur={() => setHoverIndex(null)}
        onKeyDown={onKeyDown}
      >
        {fill && geometry.area ? (
          <path d={geometry.area} fill={stroke} opacity={0.1} />
        ) : null}

        {/* Crosshair: a recessive hairline, solid - never dashed. */}
        {hoveredCoord ? (
          <line
            x1={hoveredCoord.x}
            x2={hoveredCoord.x}
            y1={0}
            y2={height}
            stroke="var(--color-axis)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}

        <path
          d={geometry.line}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* End dot, with its surface ring. The ring is drawn as a wider stroke so it
            reads at any scale, since the viewBox is stretched horizontally. */}
        <circle
          cx={last.x}
          cy={last.y}
          r={4}
          fill={stroke}
          stroke="var(--color-surface)"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />

        {hoveredCoord ? (
          <circle
            cx={hoveredCoord.x}
            cy={hoveredCoord.y}
            r={4}
            fill={stroke}
            stroke="var(--color-surface)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      {hovered ? (
        <div
          role="status"
          className="card pointer-events-none absolute -top-1 right-0 z-20 px-2 py-1 text-xs shadow-lg"
        >
          <span className="tabular font-semibold text-ink">{formatMs(hovered.value)}</span>{' '}
          <span className="text-ink-secondary">
            {valueLabel} · {formatTime(hovered.t)}
          </span>
        </div>
      ) : null}
    </div>
  )
}
